import type { VoiceLeadingDiff } from '@tunasong/music-lib'
import { ChordLib } from '@tunasong/music-lib'
import type { NoteName, Scale } from '@tunasong/schemas'
import { Popup } from '@tunasong/ui-lib'
import type { FC, KeyboardEvent, MouseEvent } from 'react'
import { useRef, useState } from 'react'
import { useShowVoiceMotion, useTheoryLevel } from '../theory-copy/use-theory-copy.js'
import { TheoryCard } from '../window-tag/theory-card.js'
import styles from './voice-leading-connector.module.css'
import {
  buildConnectorAria,
  buildConnectorLines,
  cardLexiconIds,
  lineClassKey,
  type ConnectorLine,
} from './voice-leading-connector-phrase.js'

export interface VoiceLeadingConnectorProps {
  /** The smoothest-connection diff for this chord pair, or `null` to hide. */
  diff: VoiceLeadingDiff | null
  /** Already-transposed display name of the previous chord (for the aria prefix). */
  prevName: string
  /** Already-transposed display name of the current chord (for the aria prefix). */
  currName: string
  /** Semitone transpose applied to the chips, mapped onto added/dropped note names. */
  transpose: number
  /** The chart's key: added/dropped note names move by the key interval, as the chips do (GH #2990) */
  scale?: Scale | null
  /** The previous chord as written: a dropped note is its, and is spelled as its chip spells it (GH #2990) */
  prevChord?: string
  /** The current chord as written: an added note is its, and is spelled as its chip spells it (GH #2990) */
  currChord?: string
  /**
   * Density guardrail (visual spec §4.4): when chips crowd, the host suppresses
   * the visible glyph but keeps the button + aria + hit target. Honest
   * emptiness over overlapping connectors.
   */
  dense: boolean
}

const GLYPH_WIDTH = 10
const GLYPH_HEIGHT = 16
const LINE_X0 = 1
const LINE_X1 = GLYPH_WIDTH - 1
/** How far a sloped line rises/falls from its row centre, per direction. */
const STEP_SLOPE = 1.5
const LEAP_SLOPE = 3

const slopeFor = (line: ConnectorLine): number => {
  if (line.kind === 'held' || line.kind === 'added' || line.kind === 'dropped') {
    return 0
  }
  const magnitude = line.kind === 'leap' ? LEAP_SLOPE : STEP_SLOPE
  // direction up → the right end sits higher (smaller y); down → lower.
  return line.direction === 'down' ? magnitude : -magnitude
}

export interface VoiceLeadingGlyphProps {
  lines: ConnectorLine[]
}

/**
 * Presentational (Redux-free) stacked voice-motion glyph — a 3–4-line miniature
 * of one `VoicingPianoRoll` column transition. Top voice on top; held = flat
 * solid green, steps = short dotted slopes, leaps = steeper dashed. The
 * smoothness reads as the bundle's slope envelope, not a separate badge. Split
 * out (mirroring `TensionStripe`) so Storybook can smoke it without a store.
 */
export const VoiceLeadingGlyph: FC<VoiceLeadingGlyphProps> = ({ lines }) => (
  <svg className={styles.glyph} viewBox={`0 0 ${GLYPH_WIDTH} ${GLYPH_HEIGHT}`} aria-hidden="true">
    {lines.map((line, i) => {
      // Stack rows top-down: top voice on top. Evenly distribute centres.
      const rowCentre = ((i + 0.5) / lines.length) * GLYPH_HEIGHT
      const slope = slopeFor(line)
      const y0 = rowCentre - slope / 2
      const y1 = rowCentre + slope / 2
      const className = styles[lineClassKey(line.kind)]
      return (
        <g key={`vl-line-${i}`}>
          <line x1={LINE_X0} y1={y0} x2={LINE_X1} y2={y1} className={className} />
          {line.kind === 'added' ? (
            <text x={LINE_X1} y={rowCentre + 1.5} className={styles.addedGlyph} fontSize="4">
              +
            </text>
          ) : null}
        </g>
      )
    })}
  </svg>
)

/**
 * In-editor voice-leading connector — the gutter mark between two chord chips.
 *
 * Renders nothing unless `theoryLevel !== 'off'` AND the user opted in via
 * `showVoiceMotion` AND the analyzer returned a diff (visual spec §4.2). The
 * visible glyph is a stacked mini voice-motion bundle reusing the Lab's motion
 * grammar; the smoothness reads as the bundle's slope envelope, not a badge.
 *
 * Reveal mirrors `WindowTag`: a real focusable `<button>` opening a `Popup`
 * with a level-resolved `TheoryCard`. The glyph is `aria-hidden`; meaning lives
 * in the button's bucket-vocabulary `aria-label` and the card.
 */
export const VoiceLeadingConnector: FC<VoiceLeadingConnectorProps> = props => {
  const { diff, prevName, currName, transpose, scale, prevChord, currChord, dense } = props
  const theoryLevel = useTheoryLevel()
  const showVoiceMotion = useShowVoiceMotion()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  if (theoryLevel === 'off' || !showVoiceMotion || !diff) {
    return null
  }

  /** Each note follows the chord it belongs to: an added note the current chord, a dropped note the previous one */
  const moveWith = (chord?: string) => (note: NoteName) => ChordLib.transposeToneInKey(note, transpose, scale, chord)
  const named: VoiceLeadingDiff =
    transpose === 0
      ? diff
      : { ...diff, added: diff.added?.map(moveWith(currChord)), dropped: diff.dropped?.map(moveWith(prevChord)) }

  const ariaLabel = buildConnectorAria(named, prevName, currName, note => note)
  const lines = buildConnectorLines(diff)
  // Reveal card: the surface concept first, then this pair's present concepts.
  const lexiconId = cardLexiconIds(diff)[0]

  const handleToggle = (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault()
    ev.stopPropagation()
    setOpen(prev => !prev)
  }

  const handleKeyDown = (ev: KeyboardEvent<HTMLButtonElement>) => {
    if (ev.key === 'Escape' && open) {
      ev.preventDefault()
      setOpen(false)
    }
  }

  const handleClose = () => setOpen(false)

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-expanded={open}
        className={styles.connector}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        {dense ? null : <VoiceLeadingGlyph lines={lines} />}
      </button>
      <Popup open={open} anchorEl={anchorRef.current} onClose={handleClose}>
        <TheoryCard lexiconId={lexiconId} />
      </Popup>
    </>
  )
}
